import { useMemo, useState } from 'react';
import { useProductsLookup } from '@client/store/product';
import { useRecipeLookup } from '@client/store/recipe/recipe.hook';
import { useProjectState } from '@client/store/project';
import { useSelectedVersion } from '@client/store/ui';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import {
  candidateConversion,
  candidateQuantityFrom,
  candidateUnitFrom,
  importQuantityExpression,
  isIdentityConversionFactor,
  resolveRecipeQuantityName,
  tryMaterializeCandidateRow,
} from '@shared/helpers/materialize-candidate.helpers';
import { rowWithActiveIfcMeasure } from '@shared/helpers/ifc-quantity.helpers';
import { buildReimportChanges } from '@shared/helpers/csv-import-reimport.helpers';
import { getSelectedElementQuantityProperty } from '@shared/helpers/element-quantity-core.helpers';
import { isElementQuantityExpressionName } from '@shared/helpers/element-quantity.helpers';
import { isRowDiscarded } from '@shared/helpers/candidate-discard.helpers';
import type { ConversionFactorUnit } from '@shared/models/unit.model';
import { getSelectableUnitsInConversionFactors } from '@shared/helpers/unit.helpers';
import { elementQuantityUnitRecord } from '@shared/models/element-quantities.model';
import { useElementImports } from './use-element-imports.hook';
import { useAutoImportCandidates } from './use-auto-import.hook';
import { getElementImportsData } from './element-imports.store';
import {
  collectImportedElementsInView,
  resolveCandidateProduct,
  resolveCandidateRecipe,
} from './candidate-status.helpers';
import { MappingReferenceType } from '@shared/models/element-import.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import type {
  CandidateRow,
  ElementOriginMapping,
  ResourceCodeMappingEntry,
} from '@shared/models/element-import.model';
import type { IElement } from '@shared/models/project.model';
import {
  buildImportRowDraftChange,
  draftToOriginMapping,
  getImportRowDraft,
} from '@shared/helpers/import-row-draft.helpers';
import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import { useImportRowDraft } from './use-import-row-draft.hook';
import { useListSelection } from './list-selection.store';
import { useBulkRowActions } from './use-bulk-row-actions.hook';
import { setRateSwapped, useRateSwapped } from './rate-swap.ls';
import {
  formatCandidateConversionRate,
  resolveConversionDisplayUnit,
} from './candidate-conversion.helpers';
import {
  deriveQuantityMappingEntry,
  getProductDefaultQuantityName,
} from './in-row-mapping.helpers';
import { resolveActiveIfcMeasure } from '@shared/helpers/ifc-quantity.helpers';
import {
  resolveImportQuantity,
  type ImportTarget,
} from '@shared/helpers/import-quantity-resolution.helpers';

type UseCandidateConversionEditorArgs = {
  row: CandidateRow;
  importedElement?: IElement;
};

export const useCandidateConversionEditor = ({
  row,
  importedElement,
}: UseCandidateConversionEditorArgs) => {
  const { project } = useProjectState('project');
  const imports = useElementImports(project?.id ?? -1);
  const productsById = useProductsLookup();
  const recipesById = useRecipeLookup();
  const version = useSelectedVersion();
  const applyChanges = useApplyProjectChanges();
  const { importRowWithDraft } = useAutoImportCandidates();

  // Rows of this row's own import only: a selection never spans two files, so
  // scoping by import is also the cross-import guard.
  const rowsForImport =
    imports.status === 'ready'
      ? (imports.data.rowsByImport[row.importId] ?? [])
      : [];
  const { selectedIds: storedSelection } = useListSelection();
  // The header edits the *active* row, which the store does not hold; union it
  // in exactly as useListMultiSelect does for the list.
  const selectedIds = useMemo(
    () => new Set(storedSelection).add(row.id),
    [storedSelection, row.id],
  );
  const bulk = useBulkRowActions({
    rows: rowsForImport,
    selectedIds,
    mappingsByCode:
      imports.status === 'ready' ? imports.data.mappingsByCode : {},
    importsById: imports.status === 'ready' ? imports.data.importsById : {},
  });

  const [isEditingRate, setIsEditingRate] = useState(false);
  const [rateUnitMenuAnchor, setRateUnitMenuAnchor] =
    useState<HTMLElement | null>(null);

  const origin =
    importedElement?.origin?.kind === 'csvImport'
      ? importedElement.origin
      : undefined;
  const isImported = !!origin;

  const draft = useImportRowDraft(row.id);
  const effectiveRow = useMemo(
    () => rowWithActiveIfcMeasure(row, draft.ifcMeasure),
    [row, draft.ifcMeasure],
  );
  const unitFrom = candidateUnitFrom(effectiveRow);
  const mappingsByCode =
    imports.status === 'ready' ? imports.data.mappingsByCode : {};
  const dbMapping = row.resourceCode
    ? mappingsByCode[row.resourceCode]
    : undefined;
  const draftMapping = unitFrom
    ? draftToOriginMapping(draft, unitFrom)
    : undefined;
  const overlayMapping: ElementOriginMapping | undefined = isImported
    ? origin.source === 'inline'
      ? origin.mapping
      : undefined
    : draftMapping;
  const effectiveMapping = overlayMapping ?? dbMapping;

  const savedUnitTo = unitFrom
    ? effectiveMapping?.mappings[unitFrom]?.unitTo
    : undefined;
  const referenceType = effectiveMapping?.referenceType;
  const referenceId =
    referenceType === 'product' ? effectiveMapping?.referenceId : undefined;
  const recipeReferenceId =
    referenceType === 'recipe' ? effectiveMapping?.referenceId : undefined;
  const mappedProduct =
    resolveCandidateProduct(
      effectiveRow,
      mappingsByCode,
      productsById,
      overlayMapping,
    ) ?? (referenceId ? productsById[referenceId] : undefined);
  const mappedRecipe =
    resolveCandidateRecipe(
      effectiveRow,
      mappingsByCode,
      recipesById,
      overlayMapping,
    ) ?? (recipeReferenceId ? recipesById[recipeReferenceId] : undefined);
  // The mapping's own referenceType is the discriminant — not lookup success, so
  // a recipe row stays a recipe even before recipesById has loaded the entry.
  const isRecipe = referenceType === 'recipe';
  // A recipe has no conversion factors, so the rate's unit menu offers nothing —
  // the factor is purely manual against the picked element quantity.
  const unitOptions = mappedProduct
    ? getSelectableUnitsInConversionFactors(mappedProduct.conversion_factors)
    : [];

  // The rate editor only recalibrates the factor; the picked quantity row must
  // survive into the saved entry so a DB-reused mapping keeps it.
  const savedQuantityName = unitFrom
    ? effectiveMapping?.mappings[unitFrom]?.quantityName
    : undefined;

  const elementQuantityName = importedElement
    ? getSelectedElementQuantityProperty(importedElement)?.name
    : undefined;

  // Same quantity the Amount column resolves to — omräkningsfaktor follows it.
  const amountQuantityName =
    savedQuantityName ??
    (elementQuantityName && isElementQuantityExpressionName(elementQuantityName)
      ? elementQuantityName
      : undefined) ??
    (isRecipe
      ? resolveRecipeQuantityName({
          row: effectiveRow,
          categoryId: mappedRecipe?.category_id,
          explicitQuantity: undefined,
        })
      : getProductDefaultQuantityName(mappedProduct, effectiveRow));

  const amountUnit = amountQuantityName
    ? elementQuantityUnitRecord[amountQuantityName]
    : undefined;

  const displayUnit = resolveConversionDisplayUnit({
    row: effectiveRow,
    savedUnitTo,
    amountUnit,
    isRecipe,
    savedQuantityName,
    elementQuantityName,
    recipeCategoryId: mappedRecipe?.category_id,
    productUnit: mappedProduct?.unit,
  });

  const qtyFrom = candidateQuantityFrom(effectiveRow);

  const manualFactor = unitFrom
    ? effectiveMapping?.mappings[unitFrom]?.quantityFactor
    : undefined;
  // Seed the placeholder rate in the Amount unit (m²/pcs), not the product's
  // native unit (kg/pcs), so the gray factor matches the unit selector.
  const amountAutoEntry =
    !isRecipe && amountQuantityName
      ? deriveQuantityMappingEntry({
          row: effectiveRow,
          product: mappedProduct,
          quantityName: amountQuantityName,
        })
      : null;
  const autoConversion = isRecipe
    ? undefined
    : candidateConversion(effectiveRow, mappedProduct, {
        // A typed rate owns the row's own unit; don't relabel it as a swap.
        // A file-measure pick needs no lock: it is applied to `effectiveRow`,
        // which `candidateConversion` tries before any alternate column.
        allowIfcSwap: manualFactor === undefined,
      });
  // The rate is expressed per whichever column the row actually imports
  // through, which need not be the one the parser picked. Asking the importer
  // is the only way the label cannot claim a unit the expression does not use.
  const rateSource = (() => {
    const target: ImportTarget | undefined = isRecipe
      ? mappedRecipe && { kind: 'recipe', recipe: mappedRecipe }
      : mappedProduct && { kind: 'product', product: mappedProduct };
    if (!target) return undefined;

    const resolution = resolveImportQuantity({
      row,
      target,
      pinnedMeasure: resolveActiveIfcMeasure(row, draft.ifcMeasure),
      explicitQuantity: amountQuantityName,
      manualFactor,
    });
    return resolution.status === 'resolved' ? resolution : undefined;
  })();
  // Prefer the Amount-unit factor; fall back to the legacy product-unit factor
  // (DEV-3501) when the product cannot convert into the Amount quantity.
  // Same physical unit as the CSV (m² → m²): the product already has this
  // natively — don't seed or show a * 1 rate.
  // Against the column the rate is expressed per, not the row's own: an m³
  // column feeding a volume quantity is 1:1 even when the row reads m².
  const nativeIdentity = isIdentityConversionFactor(
    rateSource?.rowUnit ?? unitFrom,
    amountUnit,
    1,
  );
  const autoFactor = nativeIdentity
    ? undefined
    : amountAutoEntry?.quantityFactor !== undefined
      ? formatCandidateConversionRate(amountAutoEntry.quantityFactor)
      : autoConversion
        ? formatCandidateConversionRate(1 / autoConversion.factor)
        : // A recipe has no conversion factors, so the harmonizer's scale
          // (ton → 1000 kg) is the whole rate.
          isRecipe && rateSource
          ? formatCandidateConversionRate(rateSource.factor)
          : undefined;
  const seededFactor = manualFactor ?? autoFactor;

  const rateSwapped = useRateSwapped(row.id);
  const prevRowId = usePrevious(row.id);
  if (prevRowId !== undefined && prevRowId !== row.id && isEditingRate) {
    setIsEditingRate(false);
  }

  const toDisplayRate = (factor: number | undefined): number | undefined =>
    factor === undefined
      ? undefined
      : formatCandidateConversionRate(rateSwapped ? 1 / factor : factor);
  const displayedRate = toDisplayRate(manualFactor);
  // Always expose the fallback: a solid manual value wins in display mode, but
  // erasing all characters while editing reveals the gray auto factor that
  // would take over on save.
  //
  // A native 1:1 rate (m² CSV into an m² Amount) is deliberately not seeded --
  // there is nothing to calibrate and `* 1` must stay out of the expression and
  // the saved entry -- but the field still has to read "1 m² per m²". The unit
  // label renders unconditionally, so a blank number beside it reads as broken.
  const placeholderRate = nativeIdentity ? 1 : toDisplayRate(autoFactor);

  const toCanonical = (value: number | undefined): number | undefined =>
    value === undefined ? undefined : rateSwapped ? 1 / value : value;

  const buildRateEntry = (
    quantityFactor: number | undefined,
    unitTo: ConversionFactorUnit | undefined = displayUnit,
  ): ResourceCodeMappingEntry => ({
    unitTo,
    quantityFactor,
    quantityName: savedQuantityName,
  });

  const persistRate = (
    canonicalFactor: number | undefined,
    unitTo?: ConversionFactorUnit,
  ) => {
    // With a multi-selection the rate applies to every selected row (DEV-3456).
    // `unitTo` is only passed when the user picked one from the menu; otherwise
    // each row keeps its own target unit and only the factor is recalibrated.
    if (bulk.shouldFanOut(row.id)) {
      bulk.setConversionRate({ quantityFactor: canonicalFactor, unitTo });
      return;
    }
    const entry = buildRateEntry(canonicalFactor, unitTo ?? displayUnit);
    const expression =
      entry.quantityFactor !== undefined && qtyFrom !== undefined
        ? importQuantityExpression(qtyFrom, entry.quantityFactor)
        : undefined;
    // Keep the row's existing target kind: a recipe row stays a recipe (the rate
    // editor only recalibrates the factor, it never converts a recipe to a product).
    const draftReferenceType: MappingReferenceType = isRecipe
      ? 'recipe'
      : 'product';
    let targetId: string | undefined;
    switch (draftReferenceType) {
      case 'recipe':
        targetId = mappedRecipe?.id ?? recipeReferenceId;
        break;
      case 'product':
        targetId = mappedProduct?.id ?? referenceId;
        break;
      default:
        assertUnreachable(draftReferenceType);
    }
    const draftPatch = {
      referenceId: targetId,
      referenceType: targetId ? draftReferenceType : undefined,
      expression,
      unitTo: entry.unitTo,
      quantityFactor: entry.quantityFactor,
      totalQuantity: undefined,
    };

    const liveElement = version
      ? collectImportedElementsInView(version).get(row.id)
      : undefined;
    if (!liveElement) {
      // Draft write + import in one batch — a single undo frame (DEV-3457).
      importRowWithDraft(row, draftPatch);
      return;
    }
    if (!version) return;
    const importsData = getElementImportsData(project?.id ?? -1);
    // Preserve the quantity row the user picked in-row; the rate editor only
    // recalibrates the factor, it must not reset the element back to its default.
    const selected = getSelectedElementQuantityProperty(liveElement)?.name;
    // Materialize with the draft being written, not the version snapshot — the
    // draft change has not been applied yet, so reading it back from `version`
    // would rebuild the element with the rate the user just replaced.
    const nextDraft = { ...getImportRowDraft(version, row.id), ...draftPatch };
    const overlay = unitFrom
      ? draftToOriginMapping(nextDraft, unitFrom)
      : undefined;
    const result = tryMaterializeCandidateRow(row, {
      mappingsByCode: importsData?.mappingsByCode ?? {},
      productsById,
      recipesById,
      importsById: importsData?.importsById ?? {},
      draftMappingsByRowId: overlay ? { [row.id]: overlay } : undefined,
      selectedQuantityByRowId:
        selected && isElementQuantityExpressionName(selected)
          ? { [row.id]: selected }
          : undefined,
      ifcMeasureByRowId: nextDraft.ifcMeasure
        ? { [row.id]: nextDraft.ifcMeasure }
        : undefined,
      trigger: 'user',
    });
    // Draft + element rebuild in one batch: one undo frame, one enriched
    // save — a separate skipEnrichment draft write could land while the
    // element change was still enriching (DEV-3457).
    const changes: ProjectChange[] = [];
    const draftChange = buildImportRowDraftChange(version, row.id, draftPatch);
    if (draftChange) changes.push(draftChange);
    let removedElement = false;
    if (result.ok) {
      // In-place: same product, so re-derive only origin + quantity and keep the
      // element id and every user edit (lifetime, overrides, etc.).
      changes.push(
        ...buildReimportChanges({
          existing: liveElement,
          fresh: result.element,
        }),
      );
    } else if (overlay) {
      // The mapping no longer materializes (e.g. a cleared rate with no
      // auto-conversion fallback): the element's mapping is incomplete, so
      // remove it — the row falls back to an incomplete candidate driven by the
      // draft just written, mirroring handleSwitchMapping (DEV-3501).
      removedElement = true;
      changes.push({
        type: ProjectChangeType.ElementRemove,
        elementId: liveElement.id,
      });
    }
    // A draft-only batch commits synchronously via skipEnrichment so the undo
    // frame is pushed immediately — no unenriched window for a quick follow-up
    // edit to lose its frame in. An element removal must enrich.
    const options =
      result.ok || removedElement ? {} : { skipEnrichment: true as const };
    if (changes.length) void applyChanges(changes, options);
  };

  const isDiscarded = isRowDiscarded(version, row.id);
  const productId = mappedProduct?.id ?? referenceId;
  const recipeId = mappedRecipe?.id ?? recipeReferenceId;
  // The mapped target id regardless of kind — drives "can save" / header gating.
  const targetReferenceId = isRecipe ? recipeId : productId;

  return {
    // The unit the rate is expressed per: a measure column names itself, so the
    // label matches the number; the row's own cells keep their raw spelling.
    unitFrom:
      rateSource?.measure && rateSource.unit ? rateSource.unit : unitFrom,
    displayUnit,
    displayedRate,
    placeholderRate,
    unitOptions,
    rateSwapped,
    isDiscarded,
    isEditingRate,
    rateUnitMenuAnchor,
    seededFactor,
    mappedProduct,
    mappedRecipe,
    isRecipe,
    productId,
    recipeId,
    referenceId: targetReferenceId,
    referenceType: isRecipe ? ('recipe' as const) : ('product' as const),
    setIsEditingRate,
    setRateUnitMenuAnchor,
    setRateSwapped: (swapped: boolean) => setRateSwapped(row.id, swapped),
    toCanonical,
    persistRate,
    buildRateEntry,
  };
};
